import React, { useState, useEffect, useRef } from 'react';
import HomepageContentService from 'services/HomepageContentService';
import Loader from 'components/UI/loader/Loader';
import 'styles/css/dynamic-homepage-content.css';

const SimpleDynamicContent = () => {
  const [content, setContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasTried, setHasTried] = useState(false);
  const contentRef = useRef(null);
  const injectedStyleIds = useRef([]);
  const injectedScriptTimeouts = useRef([]);
  const injectedScriptIntervals = useRef([]);
  const currentContentHash = useRef('');

  // Utility to create a simple hash for content
  const getContentHash = (content) => {
    if (!content) return '';
    return `${content.content || ''}`;
  };

  useEffect(() => {
    if (!hasTried) {
      setHasTried(true);
      setIsLoading(true);

      HomepageContentService.getActiveContent()
        .then(res => {
          setContent(res);
          setIsLoading(false);
        })
        .catch(error => {
          setContent(null);
          setIsLoading(false);
        });
    }
  }, [hasTried]);

  useEffect(() => {
    if (content && contentRef.current) {
      const newContentHash = getContentHash(content);
      if (newContentHash === currentContentHash.current) {
        // Content is identical, no need to re-inject
        return;
      }
      currentContentHash.current = newContentHash;

      // Cleanup previous injections
      injectedStyleIds.current.forEach(id => {
        const style = document.getElementById(id);
        if (style) style.remove();
      });
      injectedStyleIds.current = [];

      injectedScriptTimeouts.current.forEach(clearTimeout);
      injectedScriptTimeouts.current = [];
      injectedScriptIntervals.current.forEach(clearInterval);
      injectedScriptIntervals.current = [];

      try {
        contentRef.current.innerHTML = ''; // Clear previous HTML

        // Parse the combined content
        if (content.content && content.content.trim()) {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = content.content;

          // Extract and inject HTML content
          const bodyElement = tempDiv.querySelector('body');
          if (bodyElement) {
            contentRef.current.innerHTML = bodyElement.innerHTML;
          } else {
            contentRef.current.innerHTML = content.content;
          }

          // Extract and inject <style> tags with scoping to container
          const styleTags = tempDiv.querySelectorAll('style');
          styleTags.forEach((styleTag, index) => {
            const extractedStyle = document.createElement('style');
            const id = `dynamic-content-extracted-style-${Date.now()}-${index}`;
            extractedStyle.id = id;
            
            // Scope styles to the container to prevent global style conflicts
            let scopedStyleText = styleTag.textContent;
            const containerSelector = `.dynamic-content-container`;
            
            // Function to scope selectors
            const scopeSelector = (selectorText) => {
              if (!selectorText || selectorText.trim() === '') return selectorText;
              
              const trimmed = selectorText.trim();
              
              // Skip @rules declarations (they will be handled separately)
              if (trimmed.match(/^@(media|keyframes|import|charset|namespace|page|font-face|supports|document|layer)/)) {
                return selectorText;
              }
              
              // Skip if already scoped
              if (trimmed.includes(containerSelector)) {
                return selectorText;
              }
              
              // Convert :root to container selector (so CSS variables are scoped to container only)
              // This prevents CSS variables from affecting the rest of the application
              if (trimmed === ':root') {
                return containerSelector;
              }
              
              // Convert body/html to container selector (so body styles apply to container)
              if (trimmed === 'body' || trimmed === 'html') {
                return containerSelector;
              }
              
              // Scope each selector in comma-separated list
              return trimmed.split(',').map(sel => {
                const trimmedSel = sel.trim();
                // Convert :root to container (isolate CSS variables)
                if (trimmedSel === ':root') {
                  return containerSelector;
                }
                // Convert body/html to container
                if (trimmedSel === 'body' || trimmedSel === 'html') {
                  return containerSelector;
                }
                // Add container scope with higher specificity
                return `${containerSelector} ${trimmedSel}`;
              }).join(', ');
            };
            
            // Process CSS: handle @media and other @rules, then scope regular selectors
            // КРИТИЧНО: Replace all :root with container selector to isolate CSS variables
            // This prevents CSS variables from affecting the rest of the application
            scopedStyleText = scopedStyleText.replace(/:root\s*\{/g, `${containerSelector} {`);
            
            // Also handle :root in @media queries and other @rules
            scopedStyleText = scopedStyleText.replace(
              /(@media[^{]*\{[^}]*):root\s*\{/g,
              `$1${containerSelector} {`
            );
            
            // Then process all other CSS rules
            scopedStyleText = scopedStyleText.replace(
              /(@[^{]+\{[^}]*\})|([^{}@]+)(?=\s*\{)/g,
              (match, atRuleBlock, regularSelector) => {
                if (atRuleBlock) {
                  // For @rules like @media, scope selectors inside them
                  return atRuleBlock.replace(
                    /([^{}]+)(?=\s*\{)/g,
                    (innerSelector) => {
                      // Check if it's the @rule declaration itself
                      if (innerSelector.trim().match(/^@(media|keyframes|import|charset|namespace|page|font-face|supports|document|layer)/)) {
                        return innerSelector;
                      }
                      // Also replace :root inside @rules
                      const processed = innerSelector.replace(/:root\s*\{/g, `${containerSelector} {`);
                      return scopeSelector(processed);
                    }
                  );
                }
                
                if (regularSelector) {
                  return scopeSelector(regularSelector);
                }
                
                return match;
              }
            );
            
            // Add !important to critical properties to ensure they override global styles
            // This ensures that styles from dynamic content have priority over global styles
            scopedStyleText = scopedStyleText.replace(
              /(background(?:-color)?|color|border(?:-top|-bottom|left|right)?)\s*:\s*([^;]+);/gi,
              (match, property, value) => {
                // Only add !important if it's not already there and it's a critical property
                if (!value.includes('!important') && 
                    (property.toLowerCase().includes('background') ||
                     property.toLowerCase() === 'color' ||
                     property.toLowerCase().includes('border'))) {
                  return `${property}: ${value.trim()} !important;`;
                }
                return match;
              }
            );
            
            // Final check: ensure all :root are replaced (including edge cases)
            scopedStyleText = scopedStyleText.replace(/:root\s*\{/g, `${containerSelector} {`);
            
            extractedStyle.textContent = scopedStyleText;
            // Add data attribute to identify these styles
            extractedStyle.setAttribute('data-dynamic-content-style', 'true');
            document.head.appendChild(extractedStyle);
            injectedStyleIds.current.push(id);
            
            // Debug: log if :root is still present (should not happen)
            if (scopedStyleText.includes(':root {')) {
              console.warn('[Dynamic Content] Warning: :root found in scoped styles, may affect global styles');
            }
          });

          // Extract and execute <script> tags
          // Extract and execute <script> tags
          const scriptTags = tempDiv.querySelectorAll('script');
          // Контейнер для селекторов внутри динамического блока
          const container = contentRef.current;
          const CSS_ESCAPE = (typeof CSS !== 'undefined' && CSS.escape)
            ? CSS.escape
            : (s) => String(s).replace(/[^a-zA-Z0-9_\-]/g, '\\$&');

          // 🔒 Безопасная "заглушка" элемента — чтобы не падать на .addEventListener и др.
          const makeSafeEl = (el) => {
            if (el) return el;
            const noop = () => { };
            const nullProxy = new Proxy({}, {
              get(_, prop) {
                if (prop === 'addEventListener' || prop === 'removeEventListener' ||
                  prop === 'click' || prop === 'focus' || prop === 'blur' ||
                  prop === 'appendChild' || prop === 'remove' || prop === 'setAttribute') return noop;
                if (prop === 'style') return {};
                if (prop === 'classList') return { add: noop, remove: noop, toggle: noop, contains: () => false };
                if (prop === 'querySelector') return () => nullProxy;
                if (prop === 'querySelectorAll') return () => ({ length: 0, forEach: () => { } });
                return undefined;
              },
              set() { return true; }
            });
            return nullProxy;
          };

          const executeSafeJS = (jsCode) => {
            // Ждём реального рендера контейнера и выполняем код
            const wrappedCode = `
              (function() {
                // Эмулируем DOMContentLoaded если он уже произошел
                const executeCode = function() {
                  try {
                    ${jsCode}
                  } catch (error) {
                    console.error('Ошибка в пользовательском коде:', error);
                  }
                };
                
                // Если DOM уже готов, выполняем сразу, иначе ждем
                if (document.readyState === 'loading') {
                  document.addEventListener('DOMContentLoaded', executeCode);
                } else {
                  // DOM уже готов, но даем время на рендер
                  requestAnimationFrame(() => {
                    setTimeout(executeCode, 0);
                  });
                }
              })();
            `;

            // Песочница
            const safeContext = {
              console: {
                log: (...args) => console.log('[Dynamic Content]', ...args),
                error: (...args) => console.error('[Dynamic Content]', ...args),
                warn: (...args) => console.warn('[Dynamic Content]', ...args),
              },

              // 🔇 Глушим расширения и межпроцессные каналы
              chrome: undefined,
              browser: undefined,
              postMessage: undefined,

              // ✅ Разрешаем только безопасный минимум window
              window: {
                addEventListener: (...args) => window.addEventListener(...args),
                removeEventListener: (...args) => window.removeEventListener(...args),
                get innerWidth() { return window.innerWidth; },
                get scrollY() { return window.scrollY; },
                scrollTo: (...args) => window.scrollTo(...args),
                requestAnimationFrame: (cb) => window.requestAnimationFrame(cb),
                get performance() { return window.performance; },
              },

              // ⛏️ Глобальные утилиты, которые использует скрипт
              getComputedStyle: (...args) => getComputedStyle(...args),
              DOMMatrix: window.DOMMatrix || class DOMMatrix {
                constructor(str) {
                  if (str) {
                    const matrix = str.match(/matrix\(([^)]+)\)/);
                    if (matrix) {
                      const values = matrix[1].split(',').map(v => parseFloat(v.trim()));
                      this.m41 = values[4] || 0;
                      this.m42 = values[5] || 0;
                    }
                  }
                }
              },

              // 🕒 Таймеры с трекингом (как у тебя было)
              setTimeout: (fn, delay = 0) => {
                if (typeof fn === 'function') {
                  const id = setTimeout(fn, delay);
                  injectedScriptTimeouts.current.push(id);
                  return id;
                }
              },
              setInterval: (fn, delay = 1000) => {
                if (typeof fn === 'function' && delay >= 100) {
                  const id = setInterval(fn, delay);
                  injectedScriptIntervals.current.push(id);
                  return id;
                }
              },
              clearTimeout,
              clearInterval,

              // 📄 "document" ограничен контейнером + безопасные элементы
              document: {
                get readyState() { return document.readyState; },
                get documentElement() { return document.documentElement; },
                get body() {
                  return {
                    style: {
                      get overflow() { return document.body.style.overflow; },
                      set overflow(v) { document.body.style.overflow = v; }
                    }
                  };
                },
                getElementById: (id) => makeSafeEl(container?.querySelector(`#${CSS_ESCAPE(id)}`)),
                querySelector: (selector) => makeSafeEl(container?.querySelector(selector)),
                querySelectorAll: (selector) => container?.querySelectorAll(selector) || ({ length: 0, forEach: () => { } }),
                createElement: (tag) => document.createElement(tag),
                addEventListener: (event, handler, opts) => document.addEventListener(event, handler, opts),
                removeEventListener: (event, handler, opts) => document.removeEventListener(event, handler, opts),
              },
            };


            const safeFunction = new Function(
              'context',
              `with (context) { try { ${wrappedCode} } catch (error) { console.error('Ошибка в пользовательском коде:', error); } }`
            );
            safeFunction(safeContext);
          };

          // --- Исполнение каждого <script> ---
          // Выполняем скрипты после небольшой задержки, чтобы DOM точно был готов
          setTimeout(() => {
            scriptTags.forEach((scriptTag) => {
              const code = scriptTag.textContent?.trim();
              if (code) executeSafeJS(code);
            });
          }, 50);

        }
      } catch (error) {
        console.error('Ошибка рендеринга динамического контента:', error);
      }
    }

    // Cleanup on unmount or content change
    return () => {
      injectedStyleIds.current.forEach(id => {
        const style = document.getElementById(id);
        if (style) style.remove();
      });
      injectedStyleIds.current = [];

      injectedScriptTimeouts.current.forEach(clearTimeout);
      injectedScriptTimeouts.current = [];
      injectedScriptIntervals.current.forEach(clearInterval);
      injectedScriptIntervals.current = [];
    };
  }, [content]);

  if (isLoading) {
    return <Loader localStatus={true} scope="content" className="dynamic-content-loader" />; // Show loader while loading
  }

  if (!content || !content.content || !content.content.trim()) {
    return null; // Render nothing if no content
  }

  return (
    <div className="dynamic-homepage-content">
      <div ref={contentRef} className="dynamic-content-container" />
    </div>
  );
};

export default SimpleDynamicContent;